// conventions-allow: file-size — <Small add on that adds +8 rows>
import {
  ConfigurableProperty,
  DigitalTwinPropertyObject,
  DigitalTwinSettingMetadata,
  DigitalTwinSettingTarget,
  useDigitalTwin,
  useDigitalTwinSettingsActiveMetadata,
  useDigitalTwinSettingsCreatedAtMetadata,
} from 'api/digitalTwin/digitalTwin.api'
import type { DeviationSettingLevel } from 'api/digitalTwin/digitalTwin.types'
import { postStat } from 'api/stats/stats.api'
import { getPropertyTags, Options, parseTags } from 'helpers/filterComponent.helper/filterComponent.helper'
import { useActiveTags } from 'hooks/filterHooks/filterHooks'
import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useLocation, useNavigate } from 'react-router'
import { removeFilterFromUrl } from 'store/filter/urlFilter.helper'
import { Switch } from 'ui/atoms'
import Card from 'ui/atoms/Card/Card'
import Icon from 'ui/atoms/Icon/Icon'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import DigitalTwinEditSettingsModal from 'ui/components/DigitalTwinEditSettingsModal/DigitalTwinEditSettingsModal'
import { CreateSettingData } from 'ui/components/DigitalTwinSettingDialogContent/DigitalTwinSettingDialogContent'
import { CreateSettingButton } from 'ui/components/DigitalTwinSettingEditor/CreateSettingButton'
import { useTimeSeriesSettingAccess } from 'ui/components/DigitalTwinSettingForm/useTimeSeriesSettingAccess'
import FilterComponent from 'ui/components/Filter/FilterComponent'
import InfoBox from 'ui/components/ModuleHeader/InfoBox'
import ModuleHeader from 'ui/components/ModuleHeader/ModuleHeader'
import PropertiesTable from 'ui/components/PropertiesTable/PropertiesTable'
import { SettingsPermissions } from 'ui/components/SettingsTable/SettingsTable'
import SystemMessageBanner from 'ui/components/SystemMessageBanner/SystemMessageBanner'
import View from 'ui/components/View/View'
import ResetFilters from 'ui/molecules/ResetFilters/ResetFilters'
import { ToggleButton } from 'ui/molecules/ToggleButton/ToggleButton'
import Datetime from 'utils/datetime/datetime'
import {
  getFilteredProperties,
  getPropertiesFromDigitalTwinModel,
  getPropertyTableItemsForSettingMetadata,
} from 'utils/digitalTwinSettings/digitalTwinSettingUtils'
import { systemFeatureFlagActive } from 'utils/systemUtils'
import { CollectedSettingsAppliedSettingsTable } from 'views/DigitalTwinCollectedSettingsView/components/CollectedSettingsAppliedSettingsTable/CollectedSettingsAppliedSettingsTable'
import OptimizationSubtypeSelector from 'views/DigitalTwinCollectedSettingsView/components/OptimizationSubtypeSelector'
import PriorityTags from 'views/DigitalTwinCollectedSettingsView/components/PriorityTags'
import SettingPrioritySelector from 'views/DigitalTwinCollectedSettingsView/components/SettingPrioritySelector'
import {
  filterOutHiddenPriorityLevels,
  getCollectedSettingsFormPriorities,
  getPrioritiesFromPriorityLevels,
} from 'views/DigitalTwinCollectedSettingsView/DigitalTwinCollectedSettingsView.helper'
import { useCollectedSettingsFilter } from 'views/DigitalTwinCollectedSettingsView/hooks/useCollectedSettingsFilter'
import { AutomaticSettingsRunControl } from './components/AutomaticSettingsRunControl/AutomaticSettingsRunControl'
import CreateSettingModal from './components/CreateSettingModal/CreateSettingModal'
import DigitalTwinSettingsCalendar from './components/DigitalTwinSettingsCalendar/DigitalTwinSettingsCalendar'
import DigitalTwinSettingsOverviewModal from './components/DigitalTwinSettingsOverview/DigitalSettingsOverviewModal'
import ExportSettingsControl from './components/ExportSettingsControl/ExportSettingsControl'
import { SettingEditorModeSwitch } from './components/SettingEditorModeSwitch/SettingEditorModeSwitch'
import { SettingSplitView } from './components/SettingSplitView/SettingSplitView'
import { useSplitViewPosition } from './components/SettingSplitView/useSplitViewPosition'
import { SETTING_SPLIT_VIEW_SPACE_CLASS, useSplitViewWidth } from './components/SettingSplitView/useSplitViewWidth'
import {
  getActiveSettingMetadataByProperty,
  getDictFromSettingMetadata,
  isSettingMetadataPriorityIncluded,
} from './DigitalTwinSettingsView.helper'
import { isSelectedCalendarEvent, isSelectedProperty } from './settingSelectionHighlight.helper'
import { useSettingEditor } from './useSettingEditor'
import { filterBySource, getSelectedSources, useSourceFilter } from './useSourceFilter'

const OptimizeSettingHeaderContentClass = `optSettingsHeader`

function setPropertyObject(
  list: DigitalTwinPropertyObject[],
  level: DeviationSettingLevel,
  properties?: DigitalTwinPropertyObject[]
): void {
  properties?.forEach((p) => {
    p.level = level
    list.push(p)
  })
}

export function getAllFilterTags(filters: Options): string[] {
  return Object.keys(filters).flatMap((topLevel) => filters[topLevel].map((subLevel) => `${topLevel}/${subLevel}`))
}

type DigitalTwinSettingsViewProps = {
  priorities: SystemSettingPriorityLevel[]
  baseSettingPriorities?: SystemSettingPriorityLevel[]
  showCalendar?: boolean
  title: string
  calendarTitle: string
  tableTitle: string
  isBaseSettings?: boolean
  isCollectedSettings?: boolean
  permissions: SettingsPermissions
}

export default function DigitalTwinSettingsView({
  priorities,
  baseSettingPriorities,
  showCalendar = true,
  title,
  calendarTitle,
  tableTitle,
  isBaseSettings = false,
  isCollectedSettings = false,
  permissions,
}: DigitalTwinSettingsViewProps): ReactElement {
  const previousWeekReferenceTime = Datetime.subtractWeeks(Datetime.getISONow(0), 1)
  const { startTime: defaultCollectedStartTime, endTime: defaultCollectedEndTime } =
    Datetime.getStartAndEndOfTimePeriod(previousWeekReferenceTime, 'week')

  const periodStartTime = Datetime.getISONow()
  const periodEndTime = Datetime.addWeeks(periodStartTime, 1)
  const periodUpcomingStartTime = Datetime.addHours(periodStartTime, 1)

  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { activeSystem, hasAccess } = useAuth()

  const urlSearchParams = useMemo(() => new URLSearchParams(location.search), [location.search])
  const [expandAllTableRows, setExpandAllTableRows] = useState(!!urlSearchParams.get('open'))

  const primaryDigitalTwin = activeSystem?.primary_digital_twin
  const { data: digitalTwin, isLoading: loadingDigitalTwin } = useDigitalTwin(primaryDigitalTwin?.uid)

  const [filterIsCollapsed, setFilterCollapsed] = useState(true)
  const {
    containerRef: splitViewContainerRef,
    containerStyle: splitViewContainerStyle,
    ...splitViewResize
  } = useSplitViewWidth(isCollectedSettings)
  const [settingsLoaded, setSettingsLoaded] = useState(false)
  const [selectedCalendarPeriod, setSelectedCalendarPeriod] = useState<{
    startTime: ISODateTime
    endTime: ISODateTime
  } | null>(isCollectedSettings ? { startTime: defaultCollectedStartTime, endTime: defaultCollectedEndTime } : null)

  const handleCalendarPeriodChange = useCallback((startTime: ISODateTime, endTime: ISODateTime) => {
    setSelectedCalendarPeriod((previousPeriod) => {
      if (previousPeriod?.startTime === startTime && previousPeriod?.endTime === endTime) {
        return previousPeriod
      }

      return { startTime, endTime }
    })
  }, [])

  const collectedSettingsDefaultPeriod = useMemo<Pick<CreateSettingData, 'start_time' | 'end_time'>>(
    () =>
      isCollectedSettings && selectedCalendarPeriod
        ? {
            start_time: selectedCalendarPeriod.startTime,
            end_time: selectedCalendarPeriod.endTime,
          }
        : {},
    [isCollectedSettings, selectedCalendarPeriod]
  )

  const {
    collectedSettingsFilterMode,
    setCollectedSettingsFilterMode,
    availablePriorityLevels,
    setSelectedPriorityLevels,
    togglePriorityLevel,
    optimizationSubtypeOptions,
    selectedOptimizationSubtypeId,
    setSelectedOptimizationSubtypeId,
    collectedSettingsFilterSwitchItems,
    effectiveSelectedPriorityLevels,
    appliedPriorities,
  } = useCollectedSettingsFilter({
    isCollectedSettings,
    priorities,
    baseSettingPriorities,
    subtypesByOptJobType: activeSystem?.subtypes,
    translations: digitalTwin?.translations,
    t,
  })

  const selectablePriorities = useMemo(() => {
    if (!isCollectedSettings) {
      return priorities
    }

    return getCollectedSettingsFormPriorities(priorities)
  }, [isCollectedSettings, priorities])

  const sourceFilterEnabled =
    isCollectedSettings && systemFeatureFlagActive(activeSystem, 'use_setting_source_filter_dropdown')

  const displayedPriorityTags = useMemo(() => {
    const allPriorities = getPrioritiesFromPriorityLevels(availablePriorityLevels, [
      ...(baseSettingPriorities ?? []),
      ...priorities,
    ])
    if (!isCollectedSettings) {
      return allPriorities
    }

    // Hidden priority levels should not appear as priority tags in collected settings.
    const filteredLevels = filterOutHiddenPriorityLevels(availablePriorityLevels, priorities)
    const filteredSet = new Set(filteredLevels)
    return allPriorities.filter((p) => filteredSet.has(p.priority_level))
  }, [availablePriorityLevels, baseSettingPriorities, priorities, isCollectedSettings])

  const currentOptimizationPriorityLevels = useMemo(() => {
    if (!isCollectedSettings || collectedSettingsFilterMode !== 'optimization' || !selectedOptimizationSubtypeId) {
      return []
    }
    return (
      optimizationSubtypeOptions.find((option) => option.id === selectedOptimizationSubtypeId)?.priorityLevels ?? []
    )
  }, [isCollectedSettings, collectedSettingsFilterMode, selectedOptimizationSubtypeId, optimizationSubtypeOptions])

  const settingPropertyFilterEnabled = systemFeatureFlagActive(
    activeSystem,
    'digital_twin_setting_property_filter_enable'
  )

  const hasAccessToTimeseries = useTimeSeriesSettingAccess()

  const showOrEditGroupEnabled = systemFeatureFlagActive(activeSystem, 'edit_digital_twin_property_table_group')

  useEffect(() => {
    if (digitalTwin) {
      // Digital twin has changed, so we need to refetch settings. We show a loading spinner until new settings are loaded.
      setSettingsLoaded(false)
    }
  }, [digitalTwin])

  const allConfigurableProperties: ConfigurableProperty[] = useMemo(
    () => (digitalTwin?.model ? getPropertiesFromDigitalTwinModel(digitalTwin.name, digitalTwin.model) : []),
    [digitalTwin]
  )
  const {
    selection,
    revision,
    selectProperty,
    selectSetting,
    closeEditor,
    splitViewAvailable,
    opensInSplitView,
    setOpensInSplitView,
    splitViewSelection,
    highlighted,
    onShownChange,
  } = useSettingEditor({ activeSystem, isCollectedSettings, configurableProperties: allConfigurableProperties })
  const headerRef = useRef<HTMLDivElement>(null)
  useSplitViewPosition({ headerRef, containerRef: splitViewContainerRef, enabled: splitViewSelection !== null })

  const propertyItems = useMemo(() => {
    if (!digitalTwin?.model) {
      return []
    }

    const propertyObjects: DigitalTwinPropertyObject[] = []

    // model
    digitalTwin.model.name = digitalTwin.name
    digitalTwin.model.level = 'model' as DeviationSettingLevel
    propertyObjects.push(digitalTwin.model)

    // nodes & units
    digitalTwin.model.nodes.forEach((n) => {
      setPropertyObject(propertyObjects, 'unit', n.units)

      n.level = 'node' as DeviationSettingLevel
      propertyObjects.push(n)
    })

    // commodities & byproducts
    digitalTwin.model.commodities.forEach((c) => {
      setPropertyObject(propertyObjects, 'byproduct', c.byproducts)

      c.level = 'commodity' as DeviationSettingLevel
      propertyObjects.push(c)
    })

    // exchanges
    setPropertyObject(propertyObjects, 'exchange', digitalTwin.model.exchanges)

    // balances
    setPropertyObject(propertyObjects, 'balance', digitalTwin.model.balances)

    return propertyObjects
  }, [digitalTwin?.model, digitalTwin?.name])

  const propertyTags = useMemo(
    () => getPropertyTags(allConfigurableProperties, appliedPriorities),
    [allConfigurableProperties, appliedPriorities]
  )
  const filters = useMemo(() => parseTags(propertyTags), [propertyTags])

  const { availableFiltersSubset, topLevelSortingOrderOverride } = useSourceFilter({
    allConfigurableProperties,
    propertyTags,
    filters,
    sourceFilterEnabled,
    tagSortingOrder: digitalTwin?.model?.tag_sorting_order,
  })

  const { activeTags: filterTags, isActiveTagsFiltered } = useActiveTags({ availableFiltersSubset })

  const selectedSourcesFromFilter = useMemo(
    () => getSelectedSources(filterTags, sourceFilterEnabled),
    [filterTags, sourceFilterEnabled]
  )

  // This is the subset of filters that are relevant for this view.
  // All filters are not relevant in the base settings view or in the deviation settings view.
  // `filterTags` is a list of filters on the form "top_level/sub_level"
  const allFilterTags = useMemo(() => getAllFilterTags(filters), [filters])

  const [tagsLoaded, setTagsLoaded] = useState(false)

  useEffect(() => {
    // Show the filter component after we have checked the URL for a filter.
    // If we show the filters immediately, the filters will rerender after the URL is checked.
    setTagsLoaded(filters && allFilterTags.length > 0)
  }, [filters, allFilterTags])

  // Reset expandAllTableRows when filter is clicked, otherwise rows will not collapse
  useEffect(() => {
    if (!filterIsCollapsed) {
      setExpandAllTableRows(false)
    }
  }, [filterIsCollapsed])

  const [filteredProperties] = useMemo<ConfigurableProperty[][]>(
    () => getFilteredProperties(allConfigurableProperties, appliedPriorities, [...filterTags], baseSettingPriorities),
    [allConfigurableProperties, filterTags, appliedPriorities, baseSettingPriorities]
  )

  const resetFilters = useCallback((): void => {
    removeFilterFromUrl(navigate)
    setExpandAllTableRows(false)
  }, [navigate])

  // Fetch settings using the filteredProperties.
  const [settingMetadataCreatedAt, setSettingMetadataCreatedAt] = useState<DigitalTwinSettingMetadata[]>([])
  const [settingMetadataActive, setSettingMetadataActive] = useState<DigitalTwinSettingMetadata[]>([])
  const [settingMetadataActiveUpcoming, setSettingMetadataActiveUpcoming] = useState<DigitalTwinSettingMetadata[]>([])

  // Make sure we have separate `isLoading` fields for base and deviation settings.
  // If we use the same mutation for both, `isLoading` will be false when the first one is done, even if the second one is still loading.
  const { mutateAsync: fetchSettingMetadataCreatedAt, status: loadingSettingMetadataCreatedAtStatus } =
    useDigitalTwinSettingsCreatedAtMetadata()
  const { mutateAsync: fetchSettingMetadataActive, status: loadingSettingMetadataActiveStatus } =
    useDigitalTwinSettingsActiveMetadata()
  const { mutateAsync: fetchSettingMetadataActiveUpcoming, status: loadingSettingMetadataActiveUpcomingStatus } =
    useDigitalTwinSettingsActiveMetadata()

  // Setting & Base settings - Created At
  const refetchSettingMetadataCreatedAt = useCallback(() => {
    if (loadingDigitalTwin) {
      return
    }
    fetchSettingMetadataCreatedAt({
      digitalTwinUid: digitalTwin?.uid,
      priorities: appliedPriorities,
    }).then(setSettingMetadataCreatedAt)
  }, [digitalTwin?.uid, appliedPriorities, fetchSettingMetadataCreatedAt, loadingDigitalTwin])

  useEffect(refetchSettingMetadataCreatedAt, [refetchSettingMetadataCreatedAt])

  // Setting & Base settings - Currenrtly Active
  const refetchSettingMetadataActive = useCallback(() => {
    if (loadingDigitalTwin) {
      return
    }
    fetchSettingMetadataActive({
      digitalTwinUid: digitalTwin?.uid,
      priorities: baseSettingPriorities ? [...baseSettingPriorities, ...appliedPriorities] : appliedPriorities,
      startTime: periodStartTime,
      endTime: periodStartTime,
    }).then(setSettingMetadataActive)
  }, [
    digitalTwin?.uid,
    appliedPriorities,
    baseSettingPriorities,
    fetchSettingMetadataActive,
    periodStartTime,
    loadingDigitalTwin,
  ])
  useEffect(refetchSettingMetadataActive, [refetchSettingMetadataActive])

  // Setting - Active Upcoming
  const refetchSettingMetadataActiveUpcoming = useCallback(() => {
    if (loadingDigitalTwin || isBaseSettings) {
      return
    }
    fetchSettingMetadataActiveUpcoming({
      digitalTwinUid: digitalTwin?.uid,
      priorities: appliedPriorities,
      startTime: periodStartTime,
      endTime: periodEndTime,
      startFrom: periodUpcomingStartTime,
    }).then(setSettingMetadataActiveUpcoming)
  }, [
    digitalTwin?.uid,
    appliedPriorities,
    fetchSettingMetadataActiveUpcoming,
    periodStartTime,
    periodEndTime,
    periodUpcomingStartTime,
    loadingDigitalTwin,
    isBaseSettings,
  ])
  useEffect(refetchSettingMetadataActiveUpcoming, [refetchSettingMetadataActiveUpcoming])

  // Setting objects
  const sourceFilteredSettingMetadataActive = useMemo(
    () => filterBySource(settingMetadataActive, sourceFilterEnabled, selectedSourcesFromFilter),
    [settingMetadataActive, sourceFilterEnabled, selectedSourcesFromFilter]
  )

  const sourceFilteredSettingMetadataActiveUpcoming = useMemo(
    () => filterBySource(settingMetadataActiveUpcoming, sourceFilterEnabled, selectedSourcesFromFilter),
    [settingMetadataActiveUpcoming, sourceFilterEnabled, selectedSourcesFromFilter]
  )

  const sourceFilteredSettingMetadataCreatedAt = useMemo(
    () => filterBySource(settingMetadataCreatedAt, sourceFilterEnabled, selectedSourcesFromFilter),
    [settingMetadataCreatedAt, sourceFilterEnabled, selectedSourcesFromFilter]
  )

  const settingMetadataCreatedAtDict = useMemo(
    () => getDictFromSettingMetadata(sourceFilteredSettingMetadataCreatedAt),
    [sourceFilteredSettingMetadataCreatedAt]
  )

  const activeSettingByProperty = useMemo(
    () => getActiveSettingMetadataByProperty(sourceFilteredSettingMetadataActive, appliedPriorities),
    [sourceFilteredSettingMetadataActive, appliedPriorities]
  )
  const upcomingSettingByProperty = useMemo(
    () => getActiveSettingMetadataByProperty(sourceFilteredSettingMetadataActiveUpcoming, appliedPriorities),
    [sourceFilteredSettingMetadataActiveUpcoming, appliedPriorities]
  )

  // Base settings
  const baseSettingMetadataActive = useMemo(() => {
    if (baseSettingPriorities) {
      return sourceFilteredSettingMetadataActive.filter((settingMetadata) =>
        isSettingMetadataPriorityIncluded(settingMetadata, baseSettingPriorities)
      )
    } else {
      return []
    }
  }, [baseSettingPriorities, sourceFilteredSettingMetadataActive])
  const activeBaseSettingByProperty = useMemo(() => {
    if (baseSettingPriorities) {
      return getActiveSettingMetadataByProperty(baseSettingMetadataActive, baseSettingPriorities)
    } else {
      return {}
    }
  }, [baseSettingPriorities, baseSettingMetadataActive])

  // We want to show a loding spinner for every property in the table from when the page is opened until all settings are loaded.
  // There is a moment between the digital twin is loaded until the settings start loading, and we still want to show
  // the spinner in that moment.
  // So we keep it as a state variable instead of just looking at (loadingDigitalTwin || loadingBaseSettings || loadingSettings).
  useEffect(() => {
    if (
      !settingsLoaded &&
      !loadingDigitalTwin &&
      loadingSettingMetadataCreatedAtStatus === 'success' &&
      loadingSettingMetadataActiveStatus === 'success' &&
      loadingSettingMetadataActiveUpcomingStatus === 'success'
    ) {
      setSettingsLoaded(true)
    }
  }, [
    settingsLoaded,
    loadingDigitalTwin,
    loadingSettingMetadataCreatedAtStatus,
    loadingSettingMetadataActiveStatus,
    loadingSettingMetadataActiveUpcomingStatus,
  ])

  const data = useMemo(
    () =>
      getPropertyTableItemsForSettingMetadata({
        activeBaseSettingByProperty,
        activeSettingByProperty,
        digitalTwin,
        filteredProperties,
        isBaseSettings,
        periodStartTime,
        periodEndTime,
        canEdit: permissions.canEdit,
        t,
        upcomingSettingByProperty,
        setActiveSetting: selectProperty,
        loading: !settingsLoaded,
        settingMetadataCreatedAtDict,
      }),
    [
      activeBaseSettingByProperty,
      activeSettingByProperty,
      digitalTwin,
      filteredProperties,
      isBaseSettings,
      periodStartTime,
      periodEndTime,
      permissions.canEdit,
      t,
      upcomingSettingByProperty,
      settingsLoaded,
      settingMetadataCreatedAtDict,
      selectProperty,
    ]
  )

  const onEditGroup = showOrEditGroupEnabled
    ? (meta: DigitalTwinSettingTarget) => selectProperty({ level: meta.level, name: meta.name })
    : undefined

  // The settings are modified or created in children modal.
  // The toggle is used to notify other children components that also need to refetch settings.
  const [refetchTriggerToggle, setRefetchTriggerToggle] = useState(false)
  const refetchSettingsOnClose = useCallback(() => {
    refetchSettingMetadataCreatedAt()
    refetchSettingMetadataActive()
    if (!isBaseSettings) {
      refetchSettingMetadataActiveUpcoming()
    }

    setRefetchTriggerToggle((prev) => !prev)
  }, [
    isBaseSettings,
    refetchSettingMetadataActive,
    refetchSettingMetadataCreatedAt,
    refetchSettingMetadataActiveUpcoming,
  ])

  const onClickFilterCollapsed = useCallback(() => {
    postStat('/digitalTwinDeviationSettings', 'toggle-filter')

    setFilterCollapsed(!filterIsCollapsed)
  }, [filterIsCollapsed])

  const filterIcon = useMemo(
    () =>
      isActiveTagsFiltered ? (
        <Icon tooltip={{ title: t('Filters active'), placement: 'right' }} icon="fas fa-filter" />
      ) : (
        ''
      ),
    [isActiveTagsFiltered, t]
  )

  const editorModeSwitch = splitViewAvailable ? (
    <SettingEditorModeSwitch opensInSplitView={opensInSplitView} onChange={setOpensInSplitView} />
  ) : undefined

  const expandedFilter = !filterIsCollapsed && tagsLoaded && (
    <FilterComponent
      availableFiltersSubset={availableFiltersSubset}
      activeTags={filterTags}
      topLevelSortingOrderOverride={topLevelSortingOrderOverride}
    />
  )

  if (loadingDigitalTwin) {
    return <></>
  }

  if (!digitalTwin) {
    return <></>
  }

  const editorProps = {
    digitalTwin,
    propertyItems,
    permissions,
    priorities: selectablePriorities,
    baseSettingPriorities,
    isBaseSettings,
    isCollectedSettings,
    period: isCollectedSettings && selectedCalendarPeriod ? selectedCalendarPeriod : undefined,
    onSettingModified: refetchSettingsOnClose,
  }

  const settingActions = (
    <div className="flex items-center">
      {hasAccess({ submodule: 'optimizesettings_export', module: 'optimizesettings' }) && (
        <ExportSettingsControl activeSystem={activeSystem} />
      )}
      {isCollectedSettings &&
        activeSystem &&
        hasAccess({ submodule: 'optimizesettings_automatic_settings_run', module: 'collectedsettings' }) && (
          <AutomaticSettingsRunControl systemId={activeSystem.id} />
        )}
      <CreateSettingButton
        allowTimeSeries={hasAccessToTimeseries && !isBaseSettings}
        disabled={!permissions.canEdit}
        onCreate={(isTimeSeries) =>
          selectSetting({
            isTimeSeries,
            ...(isTimeSeries ? { value: [] } : {}),
            ...collectedSettingsDefaultPeriod,
          })
        }
      />
    </div>
  )

  return (
    <div ref={splitViewContainerRef} style={splitViewContainerStyle}>
      <View
        header={
          <div ref={headerRef} className={OptimizeSettingHeaderContentClass}>
            <ModuleHeader includePropertyFilter={false} secondaryChildren={expandedFilter}>
              <div className="grid w-full grid-cols-1 items-start gap-x-4 gap-y-3 md:grid-cols-[1fr_auto]">
                <div className="flex flex-wrap items-center gap-4">
                  <InfoBox infoHeader={title} />
                  {hasAccess({ submodule: 'optimizesettings_filter', module: 'optimizesettings' }) && (
                    <div>
                      <ToggleButton isCollapsed={filterIsCollapsed} onClick={onClickFilterCollapsed}>
                        <span>
                          {t('Filter')} {filterIcon}
                        </span>
                      </ToggleButton>
                    </div>
                  )}
                  {isCollectedSettings && (
                    <div className="flex flex-1 flex-wrap items-center gap-x-4 gap-y-2">
                      <Switch
                        items={collectedSettingsFilterSwitchItems}
                        value={collectedSettingsFilterMode}
                        onClick={setCollectedSettingsFilterMode}
                      />
                      {collectedSettingsFilterMode === 'deviation' ? (
                        <SettingPrioritySelector
                          priorities={priorities}
                          selectedPriorityLevels={effectiveSelectedPriorityLevels}
                          onChange={setSelectedPriorityLevels}
                        />
                      ) : (
                        <OptimizationSubtypeSelector
                          options={optimizationSubtypeOptions}
                          selectedOptionId={selectedOptimizationSubtypeId}
                          onChange={setSelectedOptimizationSubtypeId}
                        />
                      )}
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap-reverse items-center justify-end gap-x-4 gap-y-2 justify-self-end">
                  {editorModeSwitch && (
                    <div
                      className={
                        isCollectedSettings ? 'flex basis-full justify-end min-[1600px]:basis-auto' : undefined
                      }
                    >
                      {editorModeSwitch}
                    </div>
                  )}
                  {settingActions}
                </div>
                {isCollectedSettings && (
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 md:col-span-2">
                    <PriorityTags
                      priorities={displayedPriorityTags}
                      selectedPriorityLevels={effectiveSelectedPriorityLevels}
                      onToggle={togglePriorityLevel}
                    />
                  </div>
                )}
              </div>
            </ModuleHeader>
          </div>
        }
        stickyHeader
        bodyClassName={splitViewSelection ? SETTING_SPLIT_VIEW_SPACE_CLASS : undefined}
      >
        <SystemMessageBanner module={isBaseSettings ? 'optimizemodelsettings' : 'optimizesettings'} />
        {filteredProperties.length > 0 ? (
          <>
            {showCalendar && (
              <Card collapsible rememberCollapsed title={calendarTitle}>
                <DigitalTwinSettingsCalendar
                  digitalTwin={digitalTwin}
                  onPeriodChange={isCollectedSettings ? handleCalendarPeriodChange : undefined}
                  isEventHighlighted={highlighted ? (event) => isSelectedCalendarEvent(event, highlighted) : undefined}
                  onEditDeviationSetting={(setting) => {
                    if (setting) {
                      const value = typeof setting.value === 'string' ? Number(setting.value) : setting.value
                      selectSetting({ ...setting, value })
                    }
                  }}
                  configurableProperties={filteredProperties}
                  filteredProperties={filteredProperties}
                  settingPriorityLevels={appliedPriorities}
                  activeBaseSettings={baseSettingMetadataActive}
                  settingPropertyFilterEnabled={settingPropertyFilterEnabled}
                  refetchTriggerToggle={refetchTriggerToggle}
                  isCollectedSettings={isCollectedSettings}
                  currentOptimizationPriorityLevels={currentOptimizationPriorityLevels}
                  selectedSources={selectedSourcesFromFilter}
                  sourceFilterEnabled={sourceFilterEnabled}
                />
              </Card>
            )}
            <Card collapsible rememberCollapsed title={tableTitle}>
              <PropertiesTable
                dataIn={data}
                translations={digitalTwin.translations}
                priorities={appliedPriorities}
                permissions={permissions}
                editGrouping={onEditGroup}
                isRowHighlighted={highlighted ? (item) => isSelectedProperty(item.meta, highlighted) : undefined}
                isBaseSettings={isBaseSettings}
                expanded={expandAllTableRows ? expandAllTableRows : undefined} // MaterialReactTable uses undefined to not expand all rows
              />
            </Card>
          </>
        ) : (
          tagsLoaded && <ResetFilters onClick={resetFilters} />
        )}
        {isCollectedSettings && (
          <CollectedSettingsAppliedSettingsTable
            period={selectedCalendarPeriod}
            optimizationOptions={optimizationSubtypeOptions}
            selectedOptimizationId={selectedOptimizationSubtypeId}
            disabled={collectedSettingsFilterMode !== 'optimization'}
            allProperties={allConfigurableProperties}
            availableFiltersSubset={availableFiltersSubset}
            filterTags={[...filterTags]}
            isFiltered={isActiveTagsFiltered}
            translations={digitalTwin.translations ?? {}}
          />
        )}
        {selection?.kind === 'object' && (
          <DigitalTwinSettingsOverviewModal
            initData={selection}
            propertyItems={propertyItems}
            digitalTwin={digitalTwin}
            onClose={closeEditor}
            onSettingModified={refetchSettingsOnClose}
            priorities={priorities}
            activeSettingMetadataDict={activeSettingByProperty}
            activeBaseSettingMetadataDict={activeBaseSettingByProperty}
          />
        )}
        {!opensInSplitView && selection?.kind === 'property' && (
          <DigitalTwinEditSettingsModal
            {...editorProps}
            {...selection.property}
            configurableProperties={filteredProperties}
            onClose={closeEditor}
          />
        )}
        {!opensInSplitView && selection?.kind === 'setting' && (
          <CreateSettingModal
            {...editorProps}
            allowEmpty
            initData={selection.setting}
            configurableProperties={allConfigurableProperties}
            onClose={closeEditor}
          />
        )}
      </View>
      {opensInSplitView && (
        <SettingSplitView
          selection={splitViewSelection}
          selectionRevision={revision}
          onShownChange={onShownChange}
          configurableProperties={allConfigurableProperties}
          onClose={closeEditor}
          resize={splitViewResize}
          {...editorProps}
        />
      )}
    </div>
  )
}
